import type { FC, ReactNode } from "react";

import { cn } from "../../utils/general";
import { Card } from "../card";
import { FileDropzone } from "../file-dropzone";
import { Separator } from "../separator";
import { useEmailDraft } from "./email-draft-context";
import { EmailDraftFooter } from "./email-draft-footer";
import { EmailDraftHeader } from "./email-draft-header";

export interface EmailDraftCardProps {
  /** The body, rendered under the header while the draft is expanded; usually the host's `Form` around `EmailDraftContent`. */
  children: ReactNode;
  headerActions?: ReactNode;
  aboveFooter?: ReactNode;
  footerExtras?: ReactNode;
  showFooter?: boolean;
}

export const EmailDraftCard: FC<EmailDraftCardProps> = ({
  children,
  headerActions,
  aboveFooter,
  footerExtras,
  showFooter = true,
}) => {
  const { cardRef, placement, isMinimized, handleUploadAttachments, isEditable } = useEmailDraft();

  return (
    <div
      ref={cardRef}
      style={{ direction: "ltr" }}
      className={cn(
        "flex flex-col pointer-events-auto",
        placement === "floating" && "max-h-[calc(100dvh-1.5rem)] w-5/6 md:w-2/3 lg:w-48/100 grow-0 shrink-0 p-1",
        placement === "inline" && "w-full",
        placement === "floating" && isMinimized && "w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4"
      )}
    >
      <Card className={cn("flex-1 flex flex-col min-h-0", placement === "floating" ? "shadow-lg" : "shadow-sm")}>
        <FileDropzone
          onDrop={handleUploadAttachments}
          disabled={!isEditable}
          overlayClassName="z-10 inset-2!"
          className="flex flex-col max-h-full min-h-0"
        >
          <EmailDraftHeader actions={headerActions} />

          {!isMinimized && (
            <>
              {children}

              <div className="flex-0 px-6">
                <Separator />
              </div>

              {aboveFooter}

              {showFooter && <EmailDraftFooter extras={footerExtras} />}
            </>
          )}
        </FileDropzone>
      </Card>
    </div>
  );
};
